import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Descriptions, Spin, Tag, Table, Card, Button } from 'antd';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { FaUser, FaBookOpen, FaMoneyBillWave, FaInfoCircle, FaUserFriends } from 'react-icons/fa';
import { AiFillSchedule } from 'react-icons/ai';

import '../../../css/UserAdmin/Global.css';
import '../../../css/UserAdmin/ViewDetails.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

/**
 * Helper function to generate a full name from a person object.
 * Handles cases where parts of the name might be missing.
 * @param {object} person - The person object (e.g., facultyRef from faculty schedule).
 * @returns {string} The formatted full name or 'N/A' if person is null/undefined.
 */
const getFacultyFullName = (person) => {
    if (!person) return 'N/A';
    const firstName = person.firstName || '';
    const middleName = person.middleName ? ` ${person.middleName.charAt(0)}.` : ''; // Example: Show initial for middle name
    const lastName = person.lastName || '';
    const fullName = `${firstName}${middleName} ${lastName}`.trim();
    return fullName || 'Name not available'; // Fallback if all parts are empty
};

/**
 * StudentRecord component displays comprehensive details for a single enrolled student.
 * It fetches the student's enrollment record and the class schedule for their section.
 */
const StudentRecord = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const params = useParams(); // Using params object for robustness
    const studentIdFromUrl = params.id; // Extracted ID from URL

    // State for student's enrollment data
    const [studentData, setStudentData] = useState(null);
    const [loadingStudentData, setLoadingStudentData] = useState(true);
    const [studentDataError, setStudentDataError] = useState(null);

    // State for the class schedule of the student's section
    const [sectionSchedule, setSectionSchedule] = useState({
        classes: [],
        timeSlots: [],
        scheduleGrid: {},
        daysOrder: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] // Static for now
    });
    const [loadingSchedule, setLoadingSchedule] = useState(false);

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /**
     * Fetches the detailed enrollment record for the student.
     * This function is memoized and only re-created if studentIdFromUrl changes.
     */
    const fetchStudentEnrollmentDetails = useCallback(async () => {
        if (!studentIdFromUrl) {
            setStudentDataError("Student ID not provided in URL.");
            setLoadingStudentData(false);
            setStudentData(null);
            return;
        }

        console.log(`Fetching student details for ID: ${studentIdFromUrl}`);
        setLoadingStudentData(true);
        setStudentDataError(null);
        setStudentData(null); // Clear previous data while fetching
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/enrollments/${studentIdFromUrl}`);
            if (!response.ok) {
                let errMsg = `Failed to fetch student details. Status: ${response.status}`;
                try { const errData = await response.json(); errMsg = errData.message || errMsg; }
                catch (e) { const textError = await response.text(); errMsg += ` - ${textError}`; }
                throw new Error(errMsg);
            }
            const result = await response.json();
            const dataToSet = result.data ? result.data : result;
            if (!dataToSet || typeof dataToSet !== 'object' || !dataToSet._id) {
                throw new Error("Invalid student data format from server.");
            }
            setStudentData(dataToSet);
        } catch (err) {
            console.error("Error in fetchStudentEnrollmentDetails:", err);
            setStudentDataError(err.message);
            toast.error(err.message || "Could not load student details.");
            setStudentData(null);
        } finally {
            setLoadingStudentData(false);
        }
    }, [studentIdFromUrl]);

    /**
     * Fetches faculty schedules and constructs the class schedule for the student's section.
     * @param {object} enrollmentRecord - The student's fully loaded enrollment record.
     */
    const fetchSectionScheduleForStudent = useCallback(async (enrollmentRecord) => {
        if (!enrollmentRecord || !enrollmentRecord.section || !enrollmentRecord.academicYear ||
            !enrollmentRecord.academicTerm || !enrollmentRecord.academicLevel) {
            console.warn("Cannot fetch section schedule: Essential details missing from enrollment record.");
            setSectionSchedule(prev => ({ ...prev, classes: [], timeSlots: [], scheduleGrid: {} }));
            setLoadingSchedule(false);
            return;
        }
        setLoadingSchedule(true);
        try {
            const facultySchedulesRes = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/faculty-schedules`);
            if (!facultySchedulesRes.ok) throw new Error('Failed to fetch faculty schedules.');

            const facultySchedulesData = await facultySchedulesRes.json();
            const facultySchedulesArray = Array.isArray(facultySchedulesData) ? facultySchedulesData : (facultySchedulesData.data || []);

            const populatedFacultySchedules = facultySchedulesArray.map(item => ({
                ...item,
                facultyFullName: getFacultyFullName(item.facultyRef)
            }));

            const sectionClasses = populatedFacultySchedules.filter(fs =>
                fs.academicYear === enrollmentRecord.academicYear &&
                fs.academicTerm === enrollmentRecord.academicTerm &&
                fs.gradeLevel === enrollmentRecord.academicLevel &&
                fs.strand === enrollmentRecord.academicStrand &&
                fs.section === enrollmentRecord.section &&
                fs.status === 'Active' && !fs.isArchived
            ).sort((a, b) => (a.timeStart || "").localeCompare(b.timeStart || ""));

            let allTimeSlots = new Set();
            sectionClasses.forEach(cls => { allTimeSlots.add(`${cls.timeStart || 'N/A'} - ${cls.timeEnd || 'N/A'}`); });
            const sortedTimeSlots = Array.from(allTimeSlots).sort((a, b) => (a.split(' - ')[0] || "").localeCompare(b.split(' - ')[0] || ""));

            const scheduleGrid = {};
            const currentDaysOrder = sectionSchedule.daysOrder; // Use daysOrder defined in initial state

            currentDaysOrder.forEach(day => {
                scheduleGrid[day] = {};
                sortedTimeSlots.forEach(slot => {
                    scheduleGrid[day][slot] = null;
                    const [start, end] = slot.split(' - ');
                    const classInSlot = sectionClasses.find(cls => cls.days && cls.days.includes(day) && cls.timeStart === start && cls.timeEnd === end);
                    if (classInSlot) {
                        scheduleGrid[day][slot] = {
                            subject: classInSlot.subjectID,
                            faculty: classInSlot.facultyFullName,
                            room: classInSlot.room
                        };
                    }
                });
            });
            setSectionSchedule(prev => ({ ...prev, classes: sectionClasses, timeSlots: sortedTimeSlots, scheduleGrid }));
        } catch (err) {
            console.error("Error fetching/processing section schedule:", err);
            toast.error("Could not load class schedule for the section.");
            setSectionSchedule(prev => ({ ...prev, classes: [], timeSlots: [], scheduleGrid: {} }));
        } finally {
            setLoadingSchedule(false);
        }
        // Since daysOrder is static in this component's state, it doesn't need to be a dependency.
        // If daysOrder could change based on props or other state, it would be included.
    }, [sectionSchedule.daysOrder]);

    // Effect to fetch student details when studentIdFromUrl (from URL) changes.
    useEffect(() => {
        if (studentIdFromUrl) {
            fetchStudentEnrollmentDetails();
        } else {
            // No ID in URL, set error and stop loading.
            setLoadingStudentData(false);
            setStudentData(null);
            setStudentDataError("Student ID not found in the URL.");
        }
    }, [studentIdFromUrl, fetchStudentEnrollmentDetails]);

    // Effect to fetch the section's class schedule once studentData is successfully loaded.
    useEffect(() => {
        if (studentData) {
            fetchSectionScheduleForStudent(studentData);
        }
    }, [studentData, fetchSectionScheduleForStudent]);

    /**
     * Navigates the user back to the list of students.
     */
    const handleBack = () => navigate('/admin/manage-students');

    /**
     * Formats an address object into a displayable string.
     * @param {object} address - The address object from studentData.
     * @returns {string} A comma-separated address string or 'N/A'.
     */
    const renderAddress = (address) => {
        if (!address) return 'N/A';
        const parts = [address.street, address.barangay, address.city, address.province, address.zipCode];
        return parts.filter(Boolean).join(', ').trim() || 'N/A';
    };

    // Columns definition for the section's class schedule table.
    const sectionScheduleColumns = [
        { title: 'Time Slot', dataIndex: 'timeSlot', key: 'timeSlot', fixed: 'left', width: 150, className: 'schedule-timeslot-cell' },
        ...sectionSchedule.daysOrder.map(day => ({
            title: day, dataIndex: day, key: day, width: 180, className: 'schedule-day-cell',
            render: (cellData) => cellData ? (
                <div className="schedule-cell-content">
                    <Tag color="blue" style={{ marginBottom: '4px' }}>{cellData.subject || 'N/A'}</Tag>
                    <div>{cellData.faculty || 'N/A'}</div>
                    <div style={{ fontSize: '0.8em', color: 'gray' }}>Room: {cellData.room || 'N/A'}</div>
                </div>
            ) : (<div className="schedule-cell-empty">---</div>)
        }))
    ];

    // --- Conditional Rendering for different states ---
    if (loadingStudentData) {
        return (
            <div className="main main-container">
                <Header />
                <div className="main-content loading-container">
                    <Spin size="large" />
                    <p className="loading-text">Loading student details...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (studentDataError) {
        return (
            <div className="main main-container">
                <Header />
                <div className="main-content">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack} role="button" tabIndex={0} onKeyPress={handleBack}><MdOutlineKeyboardArrowLeft /></div>
                        <p className="heading">Error</p>
                    </div>
                    <div className="error-message-container">
                        <p>{studentDataError}</p>
                        <Button onClick={() => studentIdFromUrl && fetchStudentEnrollmentDetails()}>Try Again</Button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!studentData) {
        // This case implies loading is false, no error, but studentData is still null (e.g., ID was invalid but API returned 404 gracefully)
        return (
            <div className="main main-container">
                <Header />
                <div className="main-content">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack} role="button" tabIndex={0} onKeyPress={handleBack}><MdOutlineKeyboardArrowLeft /></div>
                        <p className="heading">Student Not Found</p>
                    </div>
                    <p style={{ textAlign: 'center' }}>The requested student record could not be found.</p>
                </div>
                <Footer />
            </div>
        );
    }

    // --- Main JSX for displaying student details ---
    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack} role="button" tabIndex={0} onKeyPress={handleBack}>
                            <MdOutlineKeyboardArrowLeft />
                        </div>
                        <p className="heading">View Student Record</p>
                    </div>

                    <div className="container-columns">
                        {/* Column 1: Student Personal and Guardian Information */}
                        <div className="column">
                            <Card title={<><FaUser className="section-icon" /> Personal Information</>} bordered={false} className="details-card">
                                <Descriptions bordered column={1} size="small" layout="horizontal">
                                    <Descriptions.Item label="Student ID">{studentData.studentID || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Full Name">{studentData.fullName || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Email">{studentData.email || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Mobile">{studentData.mobile || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Date of Birth">{studentData.dob ? dayjs(studentData.dob).format('MMMM D, YYYY') : 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Gender">{studentData.gender || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Address">{renderAddress(studentData.address)}</Descriptions.Item>
                                </Descriptions>
                            </Card>
                            {studentData.guardianInfo && (Object.keys(studentData.guardianInfo).some(key => studentData.guardianInfo[key])) && (
                                <Card title={<><FaUserFriends className="section-icon" /> Guardian Information</>} bordered={false} className="details-card section-gap">
                                    <Descriptions bordered column={1} size="small" layout="horizontal">
                                        <Descriptions.Item label="Name">{studentData.guardianInfo.name || 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label="Relationship">{studentData.guardianInfo.relationship || 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label="Contact">{studentData.guardianInfo.contact || 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label="Email">{studentData.guardianInfo.email || 'N/A'}</Descriptions.Item>
                                    </Descriptions>
                                </Card>
                            )}
                        </div>

                        {/* Column 2: Academic, Enrollment, and Original Application Information */}
                        <div className="column">
                            <Card title={<><FaBookOpen className="section-icon" /> Academic & Enrollment</>} bordered={false} className="details-card">
                                <Descriptions bordered column={1} size="small" layout="horizontal">
                                    <Descriptions.Item label="Academic Year">{studentData.academicYear || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Academic Term">{studentData.academicTerm || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Grade Level">{studentData.academicLevel || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Strand">{studentData.academicStrand || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Section">{studentData.section || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Voucher Type">{studentData.voucherType || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Has Reservation?">{studentData.hasReservation ? 'Yes' : 'No'}</Descriptions.Item>
                                    <Descriptions.Item label="Enrollment Status">
                                        <Tag color={studentData.enrollmentStatus === 'Complete' ? 'green' : studentData.enrollmentStatus === 'Pending' ? 'blue' : 'orange'}>
                                            {studentData.enrollmentStatus || 'N/A'}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Official Enrolled Status">
                                        <Tag color={studentData.officialEnrolledStatus === 'Complete' ? 'green' : studentData.officialEnrolledStatus === 'Pending' ? 'blue' : 'orange'}>
                                            {studentData.officialEnrolledStatus || 'N/A'}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Enrolled Date">{studentData.enrolledDate ? dayjs(studentData.enrolledDate).format('MMMM D, YYYY HH:mm') : 'N/A'}</Descriptions.Item>
                                </Descriptions>
                            </Card>
                            {studentData.originalApplicationData && (Object.keys(studentData.originalApplicationData).some(key => studentData.originalApplicationData[key])) && (
                                <Card title={<><FaInfoCircle className="section-icon" /> Original Application</>} bordered={false} className="details-card section-gap">
                                    <Descriptions bordered column={1} size="small" layout="horizontal">
                                        <Descriptions.Item label="Application ID">{studentData.originalApplicationData.applicationId || 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label="Application Date">{studentData.originalApplicationData.applicationDate ? dayjs(studentData.originalApplicationData.applicationDate).format('MMMM D, YYYY HH:mm') : 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label="Original Status">{studentData.originalApplicationData.status || 'N/A'}</Descriptions.Item>
                                    </Descriptions>
                                </Card>
                            )}
                        </div>

                        {/* Column 3: Payment Information and Section Class Schedule */}
                        <div className="column">
                            {studentData.paymentInfo && (
                                <Card title={<><FaMoneyBillWave className="section-icon" /> Payment Information</>} bordered={false} className="details-card">
                                    <Descriptions bordered column={1} size="small" layout="horizontal">
                                        <Descriptions.Item label="Reference #">{studentData.paymentInfo.referenceNumber || 'N/A'}</Descriptions.Item>
                                        {/* ... other payment details ... */}
                                        <Descriptions.Item label="Payment Mode">{studentData.paymentInfo.paymentMode || 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label="Full Payment?">{studentData.paymentInfo.isFullPayment ? 'Yes' : 'No'}</Descriptions.Item>
                                        <Descriptions.Item label="Payment Type">{studentData.paymentInfo.paymentType || 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label="Payment Method">{studentData.paymentInfo.paymentMethod || 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label="Amount Paid">{studentData.paymentInfo.paymentAmount?.toFixed(2) || 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label="Payment Date">{studentData.paymentInfo.paymentDate ? dayjs(studentData.paymentInfo.paymentDate).format('MMMM D, YYYY HH:mm') : 'N/A'}</Descriptions.Item>
                                        {studentData.paymentInfo.tuitionAndFees && (
                                            <>
                                                <Descriptions.Item label="Tuition">{studentData.paymentInfo.tuitionAndFees.tuition?.toFixed(2) || 'N/A'}</Descriptions.Item>
                                                <Descriptions.Item label="Misc.">{studentData.paymentInfo.tuitionAndFees.miscellaneous?.toFixed(2) || 'N/A'}</Descriptions.Item>
                                                <Descriptions.Item label="Other Fees">{studentData.paymentInfo.tuitionAndFees.otherFees?.toFixed(2) || 'N/A'}</Descriptions.Item>
                                                <Descriptions.Item label="Energy Fee">{studentData.paymentInfo.tuitionAndFees.energyFee?.toFixed(2) || 'N/A'}</Descriptions.Item>
                                                <Descriptions.Item label="Voucher Ded.">{studentData.paymentInfo.tuitionAndFees.voucherDeduction?.toFixed(2) || 'N/A'}</Descriptions.Item>
                                                <Descriptions.Item label="Total Assessed">{studentData.paymentInfo.tuitionAndFees.totalAmount?.toFixed(2) || 'N/A'}</Descriptions.Item>
                                            </>
                                        )}
                                    </Descriptions>
                                    {/* Installment and Remaining Payments (simplified display) */}
                                    {studentData.paymentInfo.installmentPlan && studentData.paymentInfo.installmentPlan.length > 0 && (
                                        <div className="payment-list">
                                            <strong>Installment Plan:</strong>
                                            <ul>{studentData.paymentInfo.installmentPlan.map((item, index) => <li key={`inst-${index}`}>{item.description || `Installment ${item.installmentNumber || index + 1}`}: {item.amount?.toFixed(2)} due {item.dueDate ? dayjs(item.dueDate).format('MMM D, YYYY') : 'N/A'}</li>)}</ul>
                                        </div>
                                    )}
                                    {studentData.paymentInfo.remainingPayments && studentData.paymentInfo.remainingPayments.length > 0 && (
                                        <div className="payment-list section-gap">
                                            <strong>Remaining Payments:</strong>
                                            <ul>{studentData.paymentInfo.remainingPayments.map((item, index) => <li key={`rem-${index}`} style={{ color: item.status === 'Paid' ? 'green' : 'inherit' }}>{item.description || `Payment ${item.installmentNumber || index + 1}`}: {item.amount?.toFixed(2)} due {item.dueDate ? dayjs(item.dueDate).format('MMM D, YYYY') : 'N/A'} - Status: {item.status || 'Pending'}</li>)}</ul>
                                        </div>
                                    )}
                                </Card>
                            )}

                            <Card title={<><AiFillSchedule className="section-icon" /> Section Class Schedule</>} bordered={false} className="details-card section-gap">
                                {loadingSchedule ? (
                                    <div className="loading-container-small"><Spin /> <p>Loading schedule...</p></div>
                                ) : sectionSchedule.classes && sectionSchedule.classes.length > 0 ? ( // check .classes
                                    <Table
                                        dataSource={sectionSchedule.timeSlots.map(slot => {
                                            const rowData = { timeSlot: slot, key: slot };
                                            sectionSchedule.daysOrder.forEach(day => {
                                                rowData[day] = sectionSchedule.scheduleGrid[day]?.[slot];
                                            });
                                            return rowData;
                                        })}
                                        columns={sectionScheduleColumns}
                                        bordered
                                        pagination={false}
                                        scroll={{ x: (sectionSchedule.daysOrder.length * 180) + 150 }}
                                        size="small"
                                    />
                                ) : (
                                    <p>No class schedule found for this student's section, or required details for schedule lookup are missing.</p>
                                )}
                            </Card>
                        </div>
                    </div>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default StudentRecord;